import axios from 'axios';
import ApiInstance from '@/middlewares/api-interceptore';
import { ROUTES } from './routes';
import { USER_TYPES } from '@/constants/constants';

/**
 * GET /users/me - returns { data: { firstName, lastName, fullName, phone, email } } or flat object
 */
const getMe = async () => {
  const { data } = await ApiInstance.get(ROUTES.USERS_ME);
  return data?.data ?? data;
};
const getClinics = async userType => {
  const route = userType === USER_TYPES.PATIENT ? ROUTES.PATIENT_GET_CLINICS : ROUTES.GET_CLINICS;
  const { data } = await ApiInstance.get(route);
  return data;
};
const updateClinic = async ({ clinicId, userType }) => {
  const route =
    userType === USER_TYPES.PATIENT ? ROUTES.PATIENT_SELECT_CLINIC : ROUTES.SELECT_CLINIC;
  const { data } = await ApiInstance.post(route, { clinicId });
  return data;
};
const patchPreference = async payload => {
  const { data } = await ApiInstance.patch(ROUTES.PREFERENCES, payload);
  return data;
};

/**
 * PATCH /users/profile - self-edit, any authenticated role. Only
 * firstName/lastName/email/phone are accepted.
 */
const updateMyProfile = async payload => {
  const { data } = await ApiInstance.patch(ROUTES.USERS_PROFILE, payload);
  return data;
};

const presignMyProfilePhoto = async ({ fileName, contentType }) => {
  const { data } = await ApiInstance.post(ROUTES.USER_PROFILE_PHOTO_PRESIGN, {
    fileName,
    contentType,
  });
  return data?.data ?? data;
};

// Presigned S3 POST: must bypass ApiInstance - it targets the bucket origin, not
// API_BASE_URL, and must not carry the app's Authorization header (breaks the
// signature). Field order matters for S3's POST policy: every `fields` entry
// must be appended before the file part.
const uploadToPresignedPost = async ({ url, fields, file }) => {
  const formData = new FormData();
  Object.entries(fields || {}).forEach(([key, value]) => formData.append(key, value));
  formData.append('file', file);
  try {
    await axios.post(url, formData);
  } catch (error) {
    // S3 answers with an XML body (<Code>EntityTooLarge</Code>...), not the
    // API's { message } shape - tag the error with S3's code so the caller
    // can show a clear message instead of a generic failure.
    const body = typeof error?.response?.data === 'string' ? error.response.data : '';
    error.storageErrorCode = /<Code>([^<]+)<\/Code>/.exec(body)?.[1] ?? 'UploadFailed';
    throw error;
  }
};

// Returns the full body ({ message, data }) so callers can toast the
// backend's message (e.g. "Avatar updated successfully.").
const confirmMyProfilePhoto = async ({ fileId, fileName }) => {
  const { data } = await ApiInstance.put(ROUTES.USER_PROFILE_PHOTO_BY_ID(fileId), { fileName });
  return data;
};

/** Full self-service avatar upload: presign -> upload straight to the bucket -> confirm. */
const uploadMyProfilePhoto = async file => {
  const presign = await presignMyProfilePhoto({ fileName: file.name, contentType: file.type });
  await uploadToPresignedPost({ url: presign.upload.url, fields: presign.upload.fields, file });
  return confirmMyProfilePhoto({ fileId: presign.fileId, fileName: file.name });
};

export const summaryApis = {
  getMe,
  getClinics,
  updateClinic,
  patchPreference,
  updateMyProfile,
  uploadMyProfilePhoto,
};
